import { createSelector } from 'reselect'

import API from '../../api/api'

// Action Types
const GET_BOOK_CATEGORIES = 'GET_BOOK_CATEGORIES'
const ADD_NEW_BOOK_CATEGORY = 'ADD_NEW_BOOK_CATEGORY'
const EDIT__BOOK_CATEGORY = 'EDIT_BOOK_CATEGORY'
const FETCH_ALL_BOOK_CATEGORIES = 'GET_ALL_BOOK_CATEGORIES'
const DELETE_BOOK_CATEGORY = 'DELETE_BOOK_CATEGORY'
const REORDER_BOOK_CATEGORIES = 'REORDER_BOOK_CATEGORIES'

// Initial state
const initialState = {
  loading: false,
  data: [],
}

// Reducer
const bookCategoriesReducer = (state = initialState, action) => {
  switch (action.type) {
    case GET_BOOK_CATEGORIES: {
      return {
        ...state,
        ...action.payload,
      }
    }
    case ADD_NEW_BOOK_CATEGORY: {
      return {
        ...state,
        data: [action.newCategory, ...state.data],
      }
    }
    case EDIT__BOOK_CATEGORY: {
      const data = state.data.map((category) =>
        category.id === action.editedCategory.id ? action.editedCategory : category,
      )
      return {
        ...state,
        data,
      }
    }
    case FETCH_ALL_BOOK_CATEGORIES: {
      return {
        ...state,
        data: action.payload,
      }
    }
    case DELETE_BOOK_CATEGORY: {
      const data = state.data.filter((category) => category.id !== parseInt(action.categoryId))
      return {
        ...state,
        data,
      }
    }
    case REORDER_BOOK_CATEGORIES: {
      const ordersById = new Map(action.payload.map(({ id, order }) => [id, order]))
      return {
        ...state,
        data: state.data.map((category) =>
          ordersById.has(category.id)
            ? { ...category, order: ordersById.get(category.id) }
            : category,
        ),
      }
    }
  }
  return state
}

// Actions
export const fetchAllBookingCategories = () => {
  return (dispatch) => {
    dispatch({
      type: GET_BOOK_CATEGORIES,
      payload: {
        loading: true,
      },
    })

    return API.getBookingCategories()
      .then((res) => {
        dispatch({
          type: FETCH_ALL_BOOK_CATEGORIES,
          payload: res,
        })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
      .finally(() =>
        dispatch({
          type: GET_BOOK_CATEGORIES,
          payload: {
            loading: false,
          },
        }),
      )
  }
}

export const createBookingCategory = (categoryData) => {
  return (dispatch) => {
    return API.createBookingCategory(categoryData)
      .then((res) => {
        dispatch({ type: ADD_NEW_BOOK_CATEGORY, newCategory: res })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export const editBookingCategory = (categoryData, categoryId) => {
  return (dispatch) => {
    return API.editBookingCategory(categoryData, categoryId)
      .then((res) => {
        dispatch({ type: EDIT__BOOK_CATEGORY, editedCategory: res[0] })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

// Lightweight order-only variant used by Up/Down and drag reordering, kept separate from
// editBookingCategory (the full edit-form action, still needed there to sync a genuine name
// change back into Redux). Deliberately does NOT re-dispatch the server's response on success:
// reorderBookCategoriesLocally already applied the correct order optimistically, and re-syncing
// here would let a slow response from an earlier reorder stomp a newer one that already changed
// this category's order client-side (two quick drags racing) — this PUT exists purely to
// persist, not to drive local state.
export const editBookingCategoryOrder = (id, order) => {
  return () => {
    return API.editBookingCategory({ order }, id).catch((err) => {
      return Promise.reject(err)
    })
  }
}

// Applies new `order` values to Redux state immediately, so Up/Down clicks and drag-and-drop
// reordering reflect on screen without waiting for editBookingCategory's round trip.
export const reorderBookCategoriesLocally = (updates) => ({
  type: REORDER_BOOK_CATEGORIES,
  payload: updates,
})

export const deleteBookingCategory = (categoryId) => {
  return (dispatch) => {
    return API.deleteBookingCategory(categoryId)
      .then((res) => {
        dispatch({ type: DELETE_BOOK_CATEGORY, categoryId })
        return Promise.resolve(res)
      })
      .catch((err) => {
        return Promise.reject(err)
      })
  }
}

export default bookCategoriesReducer

export const selectBookCategories = (state) => state.bookCategories.data

export const selectBookCategoryById = createSelector(
  selectBookCategories,
  (bookingCategories) => (categoryId) => {
    if (categoryId === 'new') {
      return {}
    }
    return bookingCategories.find(({ id }) => id === parseInt(categoryId)) || {}
  },
)
